export class ApiError extends Error {
  constructor(public readonly code: string, message: string) { super(message); }
}

export const AUTH_REQUIRED_EVENT = "ads-auth-required";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = init?.body == null ? init?.headers : { "content-type": "application/json", ...init.headers };
  const response = await fetch(path, {
    ...init,
    headers
  });
  const data = await response.json().catch(() => null) as any;
  if (response.status === 401 && path !== "/api/auth/login") window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  if (!response.ok) {
    const code = data?.error?.code || data?.code || "REQUEST_FAILED";
    const msg = data?.error?.message || data?.message || (typeof data?.error === "string" ? data.error : null) || `HTTP ${response.status}`;
    throw new ApiError(code, msg);
  }
  return data as T;
}

// Deduplicate concurrent GETs across pages/components. The app shell and
// detail views can mount at the same time and otherwise issue identical
// requests (especially scopes, runs and schedules). We deliberately only keep
// requests while they are in flight, so reads never become stale due to a
// client-side cache and mutations still observe fresh data immediately.
const inFlightGets = new Map<string, Promise<unknown>>();

function getOnce<T>(path: string): Promise<T> {
  const existing = inFlightGets.get(path);
  if (existing) return existing as Promise<T>;
  const promise = request<T>(path).finally(() => {
    if (inFlightGets.get(path) === promise) inFlightGets.delete(path);
  });
  inFlightGets.set(path, promise);
  return promise;
}

export const api = {
  get: <T>(path: string) => getOnce<T>(path),
  post: <T>(path: string, body: unknown = {}) => request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown = {}) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" })
};
